import React, { useState } from 'react';
import { YearData } from '../pensionCalculator.ts';
import { usePensionStore } from '../store/pensionStore.ts';
import { InputField } from './InputField.tsx';
import { CalculationBreakdown } from './CalculationBreakdown.tsx';

interface YearCardProps {
  year: YearData;
}

export const YearCard: React.FC<YearCardProps> = ({ year }) => {
  const [isCalculationExpanded, setIsCalculationExpanded] = useState(false);
  const { updateYear, removeYear } = usePensionStore();

  const handleInputChange = (field: keyof YearData, value: number) => {
    updateYear(year.id, { [field]: value });
  };

  const handleRemove = () => {
    if (window.confirm(`Remove ${year.taxYear}? This cannot be undone.`)) {
      removeYear(year.id);
    }
  };

  const getStatusColor = () => {
    if (year.shortfall > 0) return 'border-red-200 bg-red-50';
    if (year.carryForwardAvailable > 0) return 'border-green-200 bg-green-50';
    return 'border-gray-200 bg-white';
  };

  return (
    <div className={`rounded-lg border-2 ${getStatusColor()} transition-all duration-200`}>
      {/* Card Header - Always Visible */}
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              Tax Year {year.taxYear}
            </h3>
            {year.missingYearWarning && (
              <p className="text-sm text-amber-600 flex items-center gap-1">
                ⚠️ Missing full 3 previous years data, calculation may be incomplete
              </p>
            )}
          </div>

          <button
            onClick={handleRemove}
            className="text-red-400 hover:text-red-600 p-1 min-h-[44px] min-w-[44px] flex items-center justify-center"
            aria-label="Remove year"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>

        {/* Input Fields */}
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <InputField
              label="Threshold Income"
              value={year.thresholdIncome}
              onChange={(value) => handleInputChange('thresholdIncome', value)}
              placeholder="0"
              helperText="Total income including salary, bonuses, benefits in kind, rental income, etc."
            />

            <InputField
              label="Adjusted Income"
              value={year.adjustedIncome}
              onChange={(value) => handleInputChange('adjustedIncome', value)}
              placeholder="0"
              helperText="Total income after deducting both personal and employer pension contributions."
            />
          </div>

          <InputField
            label="Annual Contribution"
            value={year.contribution}
            onChange={(value) => handleInputChange('contribution', value)}
            placeholder="0"
            helperText="Total pension contributions (employer and employee) for this year"
          />

          {/* Calculated Values */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
              <p className="text-xs text-blue-600 uppercase tracking-wide font-medium">Allowance</p>
              <p className="text-lg font-semibold text-blue-700">
                £{year.taperedAllowance.toLocaleString()}
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 border">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Shortfall</p>
              <p className={`text-lg font-semibold ${year.shortfall > 0 ? 'text-red-600' : 'text-gray-900'}`}>
                £{year.shortfall.toLocaleString()}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600 uppercase tracking-wide font-medium">Carry forward from Previous</p>
              <p className="text-lg font-semibold text-green-700">
                £{year.carryForwardAvailableFromPrevious.toLocaleString()}
              </p>
            </div>

            <div className="bg-green-50 rounded-lg p-3 border border-green-200">
              <p className="text-xs text-green-600 uppercase tracking-wide font-medium">Carry forward to Next</p>
              <p className="text-lg font-semibold text-green-700">
                £{year.carryForwardAvailable.toLocaleString()}
              </p>
            </div>
          </div>

          {/* Summary Row */}
          <div className="bg-gray-100 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Total Allowance (Unused)</p>
                <p className="text-xl font-bold text-gray-900">
                  £{year.canUseThisYear.toLocaleString()}
                </p>
              </div>
              {year.carryForwardUsed > 0 && (
                <div>
                  <p className="text-sm text-gray-600">CF Used This Year</p>
                  <p className="text-xl font-bold text-blue-700">
                    £{year.carryForwardUsed.toLocaleString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Expand Calculation Button */}
          <button
            onClick={() => setIsCalculationExpanded(!isCalculationExpanded)}
            className="w-full mt-4 px-4 py-3 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg text-gray-700 font-medium transition-colors flex items-center justify-center gap-2"
          >
            <span>{isCalculationExpanded ? 'Hide' : 'Expand'} Calculation</span>
            <svg
              className={`w-4 h-4 transition-transform ${isCalculationExpanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {/* Calculation Breakdown */}
          {isCalculationExpanded && (
            <div className="border-t border-gray-200 pt-4 mt-4">
              <CalculationBreakdown year={year} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};